import { Box, Space } from '@looker/components';
import { BISlider } from '@pulsepointinc/bi-components';
import { DsTypography } from '@pulsepointinc/design-system-react';
import { type FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { rangeExpressionToArray } from '../../../../../../helpers';
import { useFilters } from '../../../../../../hooks/useFilters';
import { TOUR_STEP_IDS } from '../../../../../../providers/TourProvider';
import type { Filter } from '../../../../../../stores/filters';

interface ClinicalDecilesProps {
  onMultiFilterChange?: (filter: Filter) => void;
  filterName: string;
}

export const ClinicalDeciles: FC<ClinicalDecilesProps> = ({ onMultiFilterChange, filterName }) => {
  const { filters } = useFilters();
  const filterExpression = filters[filterName]?.expression ?? '';
  const decilesArray = rangeExpressionToArray(filterExpression);
  const [deciles, setDeciles] = useState(decilesArray.length === 2 ? decilesArray : [1, 10]);
  const initialSet = useRef(false);

  const defaultFilter = useMemo<Filter>(() => {
    return {
      filterName,
      expression: `[${deciles[0]},${deciles[1]}]`,
      enabled: true,
      expressionType: 'target',
    };
  }, [deciles, filterName]);

  useEffect(() => {
    if (onMultiFilterChange && !initialSet.current) {
      const filterToSend = filterExpression ? { ...defaultFilter, expression: filterExpression } : defaultFilter;
      onMultiFilterChange(filterToSend);
      initialSet.current = true;
    }
  }, [defaultFilter, filterExpression, onMultiFilterChange]);

  const handleSliderChange = useCallback(
    (value: number | number[]) => {
      const newDeciles: [number, number] = Array.isArray(value)
        ? [Math.round(value[0]), Math.round(value[1])]
        : [value, value];

      setDeciles(newDeciles);

      if (onMultiFilterChange) {
        const filter: Filter = {
          ...defaultFilter,
          expression: `[${newDeciles[0]},${newDeciles[1]}]`,
        };
        onMultiFilterChange(filter);
      }
    },
    [defaultFilter, onMultiFilterChange],
  );

  return (
    <Space paddingX="u6" gap="u5" data-tour-id={TOUR_STEP_IDS.CLINICAL_DECILES_FILTER}>
      <DsTypography variant="label">Decile</DsTypography>

      <BISlider dualThumb onValueChange={handleSliderChange} defaultSliderValue={deciles} min={1} max={10} />

      <Box flex="none">
        <DsTypography variant="label">{deciles.join(' - ')}</DsTypography>
      </Box>
    </Space>
  );
};
